// SPDX-License-Identifier: AGPL-3.0-or-later

import {isReleaseBuild} from '@fluxer/schema/src/domains/konvo/ClientVersion';
import {makeAutoObservable} from 'mobx';

/**
 * A response the browser may keep (WhatsApp pictures, media) can come from its HTTP cache with the
 * version header of the API build that first served it. Comparing that would announce a release
 * again after every reload until the cached copy expires.
 */
export function mayComeFromHttpCache(cacheControl: string | null | undefined): boolean {
	if (!cacheControl) return false;
	const directives = cacheControl.toLowerCase();
	if (/(^|,)\s*(no-store|no-cache)\s*(,|$)/.test(directives)) return false;
	const maxAge = /(^|,)\s*max-age\s*=\s*"?(\d+)/.exec(directives);
	return directives.includes('immutable') || (maxAge != null && Number(maxAge[2]) > 0);
}

/** API builds deploy independently of the web app. Compare with this session's first response. */
export class ApiReleaseStore {
	private endpoint: string | null = null;
	private initialVersion: string | null = null;
	version: string | null = null;
	available = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	observe(endpoint: string, version: string | null | undefined, cacheControl?: string | null): void {
		if (!isReleaseBuild(version) || mayComeFromHttpCache(cacheControl)) return;
		if (this.endpoint !== endpoint) {
			this.endpoint = endpoint;
			this.initialVersion = null;
			this.available = false;
		}
		const release = version.trim();
		if (this.initialVersion === null) {
			this.initialVersion = release;
			this.version = release;
		} else if (release !== this.initialVersion) {
			this.version = release;
			// A response from an old worker during cutover must not clear the notice.
			this.available = true;
		}
	}
}

export default new ApiReleaseStore();
